import React, { useCallback } from 'react';
import { PropTypes } from 'prop-types';
import { useNavigate } from 'react-router-dom';
import RecipeCollectionsAddMealCard from './recipes_collections_add_meal_card';
import { targetDayNutritionFromMetricConstraints } from '../../services/nutrition';
import { DEFAULT_PROGRAMME_CALORIE_SPLITS } from '../../services/meal_types';
import {
  dayIndexForCalendarDate,
  dbWeekStartDateStr,
  plannerWeekWithMealAdded,
  plannerWeekWithMealReplaced,
  viewWeekStartDateStr,
} from '../../services/planner';
import {
  useCreateMealMutation,
  useDeleteMealMutation,
  useGetDbPlannerWeekQuery,
  useGetSharedBoardsByIDsQuery,
  useGetSharedProgrammeQuery,
  useLazyGetFullMealQuery,
  useLazyGetFullSharedMealQuery,
  useUpdateDbPlannerWeekOptimisticallyMutation,
  useUpdateMealMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import {
  parentIDOfMealID,
  parentIDOfSharedMealID,
} from '../../services/smorg_board';
import { currentProgrammeTargetCalories } from '../../services/end_user_space_memberships';
import { cloneObject } from '../../operations/utils';
import {
  CURRENT_MEAL_SCHEMA_VERSION,
  mealFromSharedMeal,
  mealWithScaledIngredientsReplaced,
} from '../../services/meals';
import {
  scaleMealIngredientsOperation,
  updateFoodBrainDerivedDataOnMeal,
} from '../../operations/meal_operations';
import { customerFoodDataIDOfSpace } from '../../services/spaces';
import {
  useCurrentProgrammeEnrollmentQuery,
  useGetCurrentSpaceMembershipQuery,
  useGetDistilledMealByIDAndParentQuery,
  useGetOwnedRecipesBoardsQuery,
  useGetDistilledSharedMealByIDAndParentQuery,
  useGetSpaceAssignedSharedRecipesBoardIDsForEndUserSearchQuery,
  useGetHealthProBoardsQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import {
  SHARED_BOARDS_REFRESH_INTERVAL_SECONDS,
  SHARED_PROGRAMMES_REFRESH_INTERVAL_SECONDS,
} from '../../services/end_user_published_resources';

const EndUserFindAlternativesItem = ({
  mealID,
  calendarDay,
  mealType,
  mealIDToReplace,
  onClick,
}) => {
  const navigate = useNavigate();

  const { data: currentSpaceMembership } = useGetCurrentSpaceMembershipQuery();

  const customerFoodDataID = customerFoodDataIDOfSpace(
    currentSpaceMembership?.spaceID,
  );

  const { data: ownedRecipesBoards } = useGetOwnedRecipesBoardsQuery();

  const parentIDInMeals = parentIDOfMealID(mealID, ownedRecipesBoards);

  const {
    data: spaceAssignedSharedRecipesBoardsIDs,
    isLoading: isSaceAssignedSharedRecipesBoardsIDsLoading,
  } = useGetSpaceAssignedSharedRecipesBoardIDsForEndUserSearchQuery();

  const { data: healthProBoards } = useGetHealthProBoardsQuery();

  const parentIDInHealthProMeals = parentIDOfMealID(mealID, healthProBoards);

  const { data: sharedBoards } = useGetSharedBoardsByIDsQuery(
    spaceAssignedSharedRecipesBoardsIDs,
    {
      skip:
        isSaceAssignedSharedRecipesBoardsIDsLoading ||
        !spaceAssignedSharedRecipesBoardsIDs ||
        spaceAssignedSharedRecipesBoardsIDs.length === 0,
      refetchOnMountOrArgChange: SHARED_BOARDS_REFRESH_INTERVAL_SECONDS,
    },
  );

  const parentIDInSharedMeals = parentIDOfSharedMealID(mealID, sharedBoards);

  const { data: distilledMealInMeals } = useGetDistilledMealByIDAndParentQuery(
    { id: mealID, parentID: parentIDInMeals },
    { skip: !mealID || !parentIDInMeals },
  );

  const { data: distilledMealInHealthProMeals } =
    useGetDistilledMealByIDAndParentQuery({
      id: mealID,
      parentID: parentIDInHealthProMeals,
    });

  const { data: distilledMealInSharedMeals } =
    useGetDistilledSharedMealByIDAndParentQuery({
      id: mealID,
      parentID: parentIDInSharedMeals,
    });

  const distilledMeal =
    distilledMealInMeals ||
    distilledMealInHealthProMeals ||
    distilledMealInSharedMeals;

  const { data: currentProgrammeEnrollment } =
    useCurrentProgrammeEnrollmentQuery();

  const { data: sharedProgramme } = useGetSharedProgrammeQuery(
    currentProgrammeEnrollment?.sharedProgrammeID,
    {
      skip: !currentProgrammeEnrollment?.sharedProgrammeID,
      refetchOnMountOrArgChange: SHARED_PROGRAMMES_REFRESH_INTERVAL_SECONDS,
    },
  );

  const personalisedMealScaling =
    sharedProgramme?.personalisedMealScaling || false;

  const programmeCalorieSplits =
    sharedProgramme?.calorieSplits || DEFAULT_PROGRAMME_CALORIE_SPLITS;

  const calorieSplitFraction =
    mealType &&
    programmeCalorieSplits.find((split) => split.mealType === mealType)
      ?.fraction;

  const targetCalories = currentProgrammeTargetCalories(currentSpaceMembership);

  const { nutritionConstraints } = sharedProgramme || {};

  const targetDayNutrition = targetDayNutritionFromMetricConstraints(
    nutritionConstraints?.nutritionMetricConstraints || [],
    ['calories'],
    targetCalories,
  );

  const targetDayCalories =
    targetDayNutrition?.calories?.targetQuantity || targetCalories;

  const targetCaloriesForScaling =
    personalisedMealScaling &&
    targetDayCalories &&
    calorieSplitFraction &&
    targetDayCalories * calorieSplitFraction;

  // console.log({
  //   personalisedMealScaling,
  //   targetCalories,
  //   targetDayNutrition,
  //   targetDayCalories,
  //   calorieSplitFraction,
  //   targetCaloriesForScaling,
  // });

  const plannerViewWeekStartDay = 1; /* plannerViewWeekStartDayOrDefault(
    userProfile?.plannerViewWeekStartDay,
  ); */

  const viewWeekStartDate = viewWeekStartDateStr(
    calendarDay,
    plannerViewWeekStartDay,
  );

  const viewWeekDayIndex = dayIndexForCalendarDate(calendarDay);

  const { data: plannerWeek } = useGetDbPlannerWeekQuery(
    dbWeekStartDateStr(calendarDay),
    {
      skip: !calendarDay,
    },
  );

  const [createMeal] = useCreateMealMutation();

  const [updateMeal] = useUpdateMealMutation();

  const [deleteMeal] = useDeleteMealMutation();

  const [updateDbPlannerWeek] = useUpdateDbPlannerWeekOptimisticallyMutation();

  const [lazyGetMeal] = useLazyGetFullMealQuery();

  const [lazyGetSharedMeal] = useLazyGetFullSharedMealQuery();

  const onAdd = useCallback(async () => {
    navigate(`/planner/${viewWeekStartDate}#${viewWeekDayIndex}`);
    let newFullMeal;
    if (distilledMealInMeals || distilledMealInHealthProMeals) {
      const { data: fullMeal } = await lazyGetMeal(mealID);
      newFullMeal = cloneObject(fullMeal);
    } else if (distilledMealInSharedMeals) {
      const { data: fullSharedMeal } = await lazyGetSharedMeal(mealID);
      newFullMeal = mealFromSharedMeal(fullSharedMeal);
    } else {
      console.warn(
        `Asked to insert meal ${mealID} but I could not find this meal`,
      );
      return;
    }
    const originalCalories =
      newFullMeal?.derivedNutrition?.totalNutritionPerServing?.calories ||
      (newFullMeal?.recipes || [])[0]?.nutrition?.calories;
    const shouldScale =
      targetCaloriesForScaling &&
      originalCalories &&
      originalCalories > 0 &&
      targetCaloriesForScaling > 0;
    if (shouldScale) {
      const scaleFactor = targetCaloriesForScaling / originalCalories;
      console.log({ scaleFactor });
      const scaledIngredients = await scaleMealIngredientsOperation(
        newFullMeal,
        scaleFactor,
      );
      const { servings } = newFullMeal.recipes[0];
      newFullMeal = mealWithScaledIngredientsReplaced(
        newFullMeal,
        servings,
        scaledIngredients,
      );
    }
    const input = {
      ...newFullMeal,
      schemaVersion: CURRENT_MEAL_SCHEMA_VERSION,
      smorgBoardID: plannerWeek.id,
      spaceMembershipID: currentSpaceMembership.id,
    };
    const { data: insertedMeal } = await createMeal(input);
    if (mealIDToReplace) {
      await deleteMeal({ id: mealIDToReplace, parentID: plannerWeek.id });
    }
    let updatedPlannerWeek;
    if (mealIDToReplace) {
      updatedPlannerWeek = plannerWeekWithMealReplaced(
        plannerWeek,
        mealIDToReplace,
        insertedMeal,
        viewWeekDayIndex,
      );
    } else {
      updatedPlannerWeek = plannerWeekWithMealAdded(
        plannerWeek,
        insertedMeal,
        viewWeekDayIndex,
      );
    }
    // console.log(JSON.stringify(updatedPlannerWeek));
    await updateDbPlannerWeek(updatedPlannerWeek);
    await updateFoodBrainDerivedDataOnMeal(
      insertedMeal,
      customerFoodDataID,
      updateMeal,
    );
  }, [
    createMeal,
    currentSpaceMembership.id,
    customerFoodDataID,
    deleteMeal,
    distilledMealInHealthProMeals,
    distilledMealInMeals,
    distilledMealInSharedMeals,
    lazyGetMeal,
    lazyGetSharedMeal,
    mealID,
    mealIDToReplace,
    navigate,
    plannerWeek,
    targetCaloriesForScaling,
    updateDbPlannerWeek,
    updateMeal,
    viewWeekDayIndex,
    viewWeekStartDate,
  ]);

  if (!distilledMeal) {
    console.log(`EndUserFindAlternativesItem mealID=${mealID} not found`);
    return null;
  }

  return (
    <RecipeCollectionsAddMealCard
      title={distilledMeal.recipes[0].title}
      imageUrl={distilledMeal.recipes[0].imageUrl}
      onAdd={onAdd}
      onClick={() => onClick(mealID)}
      addEnabled={!!plannerWeek}
    />
  );
};

EndUserFindAlternativesItem.propTypes = {
  mealID: PropTypes.string.isRequired,
  calendarDay: PropTypes.string.isRequired,
  mealType: PropTypes.string,
  mealIDToReplace: PropTypes.string,
  onClick: PropTypes.func.isRequired,
};

EndUserFindAlternativesItem.defaultProps = {
  mealType: null,
  mealIDToReplace: null,
};

export default EndUserFindAlternativesItem;
